//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { SchemaDescription, SchemaFieldDescription } from 'yup'
import * as React from 'react'
import { ReactElement } from 'react'
import { InputAdornment, TextField, Typography } from '@mui/material'
import { FieldMetaData, StringTypeEnum } from '@shared/SchemaTypes'
import { EthAddr } from '@shared/SchemaTypes'
import getDataValue from './getDataValue'

import { commaDecStringToErcDecString, toCommaDecString } from '@shared/conversions'
import { textFieldValueToERC20DecString } from '@react/utils/textFieldsUtils'
import { logOT } from '@shared/loggingShared'

interface EditFieldProps {
  fieldSchema: SchemaFieldDescription
  initialValues?: Record<string, object> | object
  onChange?: (e: Event) => void
  register: any
  errors: any
  setValue?: any
  addr?: EthAddr
  currency?: string
}

export default function EditNumber(props: EditFieldProps): ReactElement {
  const { fieldSchema, initialValues, errors, register, currency } = props
  const castErrors = errors as Record<string, Error>
  const schemaDescription = fieldSchema as SchemaDescription
  const fieldMetaData = schemaDescription.meta as FieldMetaData

  const propName: string = fieldMetaData.propName
  const defaultValue = getDataValue(initialValues as Record<string, object | string>, propName)
  logOT.debug('defaultValue', defaultValue, initialValues, propName)
  const [value, setValue] = React.useState<string>(Number(defaultValue).toLocaleString())
  logOT.debug('Default Value ', propName, defaultValue)
  const { onChange, onBlur, name, ref } = register(propName)

  const label = schemaDescription.label ?? ''

  const handleOnFocus = (_: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(commaDecStringToErcDecString(value))
  }
  const handleOnBlur = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(toCommaDecString(value))
    onBlur(event)
  }
  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setValue(textFieldValueToERC20DecString(event))
    onChange(event)
  }

  return (
    <React.Fragment>
      <TextField
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={
          fieldMetaData.stringType === StringTypeEnum.STABLE_COINS
            ? {
                endAdornment: <InputAdornment position="start">{currency ?? 'USDC'}</InputAdornment>,
              }
            : fieldMetaData.stringType === StringTypeEnum.BPS
              ? {
                  endAdornment: <InputAdornment position="start">BPS</InputAdornment>,
                }
              : fieldMetaData.stringType === StringTypeEnum.TIME_INTERVAL_DAYS
                ? {
                    endAdornment: <InputAdornment position="start">Days</InputAdornment>,
                  }
                : fieldMetaData.stringType === StringTypeEnum.APR
                  ? {
                      endAdornment: <InputAdornment position="start">%</InputAdornment>,
                    }
                  : fieldMetaData.stringType === StringTypeEnum.OFFCHAIN_RATE
                    ? {
                        endAdornment: <InputAdornment position="start">BPS</InputAdornment>,
                      }
                    : {}
        }
        inputProps={{
          'data-testid': propName,
        }}
        required
        id={propName}
        value={value}
        label={label}
        fullWidth
        placeholder={'1234'}
        margin="dense"
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        name={name}
        ref={ref}
        error={castErrors[propName] != null}
      />
      <Typography variant="inherit" color="textSecondary">
        {errors[propName]?.message}
      </Typography>
    </React.Fragment>
  )
}
